/**
 * Beauty Warehouse — Search Modal
 * Predictive search: desktop centred popout, mobile full-screen drawer.
 * Shows default products from /collections/all on open (before typing).
 * Uses Shopify's /search/suggest.json endpoint for live search.
 */
(function () {
  if (typeof document === 'undefined') return;

  var debounceTimer = null;
  var cache = {};
  var defaultProducts = null;
  var defaultLoading = false;

  function getModal() { return document.querySelector('[data-search-modal]'); }

  /* ── Open / Close ───────────────────────────── */
  function open() {
    var modal = getModal();
    if (!modal) return;
    modal.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';
    var input = modal.querySelector('[data-search-input]');
    if (input) { input.value = ''; input.focus(); }
    showDefaults();
  }

  function close() {
    var modal = getModal();
    if (!modal) return;
    modal.removeAttribute('open');
    document.documentElement.style.overflow = '';
  }

  function clearResults() {
    var r = document.querySelector('[data-search-results]');
    if (r) r.innerHTML = '';
    var f = document.querySelector('[data-search-footer]');
    if (f) f.classList.add('hidden');
  }

  /* ── Default products ──────────────────────── */
  function showDefaults() {
    if (defaultProducts) {
      renderDefaults(defaultProducts);
      return;
    }
    if (defaultLoading) return;
    defaultLoading = true;

    var container = document.querySelector('[data-search-results]');
    if (container) container.innerHTML = '<div class="search-modal__loading">Loading…</div>';

    fetch('/collections/all/products.json?sort_by=best-selling&limit=8', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        defaultProducts = data.products ? data.products.slice(0, 8) : [];
        defaultLoading = false;
        renderDefaults(defaultProducts);
      })
      .catch(function () {
        defaultLoading = false;
        /* Fallback: try predictive search with a broad term */
        fetch('/search/suggest.json?q=*&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var products = data.resources && data.resources.results && data.resources.results.products
              ? data.resources.results.products : [];
            /* Normalise to collection-API shape */
            defaultProducts = products.map(function (p) {
              return {
                title: p.title,
                handle: p.handle,
                url: p.url,
                images: p.image ? [{ src: p.image }] : [],
                variants: [{ price: p.price }]
              };
            });
            renderDefaults(defaultProducts);
          })
          .catch(function () {
            clearResults();
          });
      });
  }

  function renderDefaults(products) {
    var container = document.querySelector('[data-search-results]');
    var footer = document.querySelector('[data-search-footer]');
    if (!container) return;

    if (!products || products.length === 0) {
      container.innerHTML = '';
      if (footer) footer.classList.add('hidden');
      return;
    }

    var html = '<p class="search-modal__section-title">Popular products</p>';
    html += '<div class="search-modal__grid">';

    products.forEach(function (product) {
      var imgSrc = '';
      if (product.images && product.images.length > 0) {
        imgSrc = product.images[0].src || '';
        /* Resize for performance */
        imgSrc = imgSrc.replace(/\.(jpg|jpeg|png|gif|webp)(\?)?/i, '_360x.$1$2');
      } else if (product.featured_image) {
        imgSrc = product.featured_image.replace(/\.(jpg|jpeg|png|gif|webp)(\?)?/i, '_360x.$1$2');
      }

      var img = imgSrc
        ? '<img src="' + imgSrc + '" alt="' + esc(product.title) + '" loading="lazy">'
        : '<div class="search-modal__placeholder"></div>';

      var price = '';
      if (product.variants && product.variants.length > 0) {
        price = fmtMoney(product.variants[0].price);
      }

      var url = product.url || ('/products/' + product.handle);

      html += '<a href="' + url + '" class="search-modal__product">'
        + '<div class="search-modal__product-image">' + img + '</div>'
        + '<p class="search-modal__product-title">' + esc(product.title) + '</p>'
        + '<p class="search-modal__product-price">' + price + '</p>'
        + '</a>';
    });

    html += '</div>';
    container.innerHTML = html;

    /* Hide footer for defaults */
    if (footer) footer.classList.add('hidden');
  }

  /* ── Events (delegated) ────────────────────── */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-search-trigger]')) { e.preventDefault(); open(); return; }
    if (e.target.closest('[data-search-close]'))   { close(); return; }
  });

  document.addEventListener('keydown', function (e) {
    var modal = getModal();
    if (e.key === 'Escape' && modal && modal.hasAttribute('open')) close();
  });

  /* Search input — debounced */
  document.addEventListener('input', function (e) {
    if (!e.target.matches('[data-search-input]')) return;
    var query = e.target.value.trim();
    clearTimeout(debounceTimer);
    if (query.length < 2) {
      if (query.length === 0) showDefaults();
      else clearResults();
      return;
    }
    debounceTimer = setTimeout(function () { performSearch(query); }, 300);
  });

  /* Submit search form on Enter */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    if (!e.target.matches('[data-search-input]')) return;
    var query = e.target.value.trim();
    if (query.length > 0) {
      close();
      window.location.href = '/search?q=' + encodeURIComponent(query);
    }
  });

  /* ── Predictive search ─────────────────────── */
  function performSearch(query) {
    if (cache[query]) { renderResults(cache[query], query); return; }

    var url = '/search/suggest.json?q=' + encodeURIComponent(query)
      + '&resources[type]=product'
      + '&resources[limit]=8'
      + '&resources[options][unavailable_products]=last';

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = data.resources && data.resources.results && data.resources.results.products
          ? data.resources.results.products : [];
        cache[query] = products;
        renderResults(products, query);
      })
      .catch(function (err) { console.error('Search error:', err); });
  }

  function renderResults(products, query) {
    var container = document.querySelector('[data-search-results]');
    var footer = document.querySelector('[data-search-footer]');
    if (!container) return;

    if (products.length === 0) {
      container.innerHTML = '<p class="search-modal__no-results">No products found</p>';
      if (footer) footer.classList.add('hidden');
      return;
    }

    var html = '<p class="search-modal__section-title">Products</p>';
    html += '<div class="search-modal__grid">';

    products.forEach(function (product) {
      var imgUrl = product.image ? product.image.replace(/\.(jpg|jpeg|png|gif|webp)(\?)?/i, '_360x.$1$2') : '';
      var img = imgUrl
        ? '<img src="' + imgUrl + '" alt="' + esc(product.title) + '" loading="lazy">'
        : '<div class="search-modal__placeholder"></div>';
      var price = product.price ? fmtMoney(product.price) : '';

      html += '<a href="' + product.url + '" class="search-modal__product">'
        + '<div class="search-modal__product-image">' + img + '</div>'
        + '<p class="search-modal__product-title">' + esc(product.title) + '</p>'
        + '<p class="search-modal__product-price">' + price + '</p>'
        + '</a>';
    });

    html += '</div>';
    container.innerHTML = html;

    /* View all link */
    if (footer) {
      footer.classList.remove('hidden');
      var link = footer.querySelector('[data-search-view-all]');
      if (link) link.href = '/search?q=' + encodeURIComponent(query);
    }
  }

  /* Money formatter — handles both cents (collection API) and string price (predictive search) */
  function fmtMoney(price) {
    var fmt = window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : 'R {{amount}}';
    var num = typeof price === 'string' ? parseFloat(price) : price;
    /* Collection API returns price in money units (e.g. "399.00"), predictive search also returns string */
    var str = num.toFixed(2);
    var noDecimals = Math.round(num);
    return fmt
      .replace('{{amount_with_comma_separator}}', str.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimals.toLocaleString())
      .replace('{{amount_no_decimals}}', noDecimals)
      .replace('{{amount}}', str);
  }

  function esc(s) {
    var el = document.createElement('span');
    el.textContent = s;
    return el.innerHTML;
  }

  window.searchModal = { open: open, close: close };
})();
